import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaFileInvoiceDollar,
    FaCheckCircle,
    FaTimesCircle,
    FaCloudUploadAlt,
    FaCloudDownloadAlt,
    FaReceipt,
    FaTags,
    FaArrowLeft
} from "react-icons/fa";
import { BiFile } from "react-icons/bi";
import api from "./api/api";

const UploadPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const [tableData, setTableData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [sumTotal, setSumTotal] = useState("0.00");
    const [receiptData, setReceiptData] = useState(null);
    const [file, setFile] = useState(null);
    const [isPdfJsLoaded, setIsPdfJsLoaded] = useState(false);
    const fileInputRef = useRef(null);

    const tableHeaders = [
        "S.No", "Doc. Type", "Document No", 
        "CMI Code", "Doc.Text", "Bill.No", "Bill.Date", "Gross", "TDS", "Net.Amt", "D/C"
    ];

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js";
        script.onload = () => {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
            setIsPdfJsLoaded(true);
        };
        document.head.appendChild(script);
        return () => {
            try {
                document.head.removeChild(script);
            } catch (e) {
                console.error(e);
            }
        };
    }, []);

    useEffect(() => {
        if (message && (messageType === "success" || messageType === "error")) {
            const timer = setTimeout(() => setMessage(""), 5000);
            return () => clearTimeout(timer);
        }
    }, [message, messageType]);

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.type === "application/pdf") {
            setFile(selectedFile);
            setMessage("");
            setMessageType("info");
            setRowCount(0);

            const user = JSON.parse(sessionStorage.getItem("user") || "{}");
            const username = user?.UsrName || "";
            if (!username) {
                navigate("/LoginPage", { replace: true });
                return;
            }

            setLoading(true);
            setTableData([]);
            setSumTotal("0.00");
            setReceiptData(null);
            setMessage("Processing your PDF...");
            setMessageType("info");

            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const { extractedRows, total, commonDetails } = await parsePDF(reader.result);
                    setTableData(extractedRows);
                    setSumTotal(total);
                    setRowCount(extractedRows.length);
                    setReceiptData(commonDetails);

                    if (extractedRows.length > 0) {
                        setMessage(`Successfully extracted ${extractedRows.length} rows.`);
                        setMessageType("success");
                    } else {
                        setMessage("No valid table rows were found in the PDF.");
                        setMessageType("error");
                    }
                } catch (err) {
                    console.error("Error processing PDF:", err);
                    setMessage(`Error parsing PDF: ${err.message}`);
                    setMessageType("error");
                } finally {
                    setLoading(false);
                }
            };
            reader.readAsArrayBuffer(selectedFile);
        } else {
            setFile(null);
            setMessage("Please select a valid PDF file.");
            setMessageType("error");
            setRowCount(0);
            setSumTotal("0.00");
            setReceiptData(null);
        }
    };

    const handlePostData = async () => {
        setLoading(true);
        setMessage("Sending data to API...");
        setMessageType("info");

        const user = JSON.parse(sessionStorage.getItem("user") || "{}");
        const username = user?.UsrName || "";

        const apiPayload = tableData.map((row, index) => ({
            CustomerName: row["Customer Name"],
            CustomerAddress: row["Customer Address"],
            VoucherNo: row["Voucher No"],
            VoucherDate: row["Voucher Date"],
            ChequeNo: row["Cheque No"],
            Supplier: row["Supplier Details"],
            Slno: row["S.No"].toString(),
            DocType: row["Doc. Type"],
            DocNo: row["Document No"],
            PostingDate: row["Posting Date"],
            CMICode: row["CMI Code"],
            DocText: row["Doc.Text"],
            BillNo: row["Bill.No"],
            BillDate: row["Bill.Date"],
            Amount: parseFloat(row["Gross"]),
            TDS: parseFloat(row["TDS"]),
            NetTotal: parseFloat(row["Net.Amt"]),
            AmtType: row["D/C"],
            SumTotal: index === 0 ? parseFloat(sumTotal) : 0.0,
        }));

        try {
            const response = await api.insertPdfReceiptData(username, apiPayload);
            if (response?.Status === "Error") {
                setMessage(response.Message || "Something went wrong!");
                setMessageType("error");
            } else {
                setMessage("Data successfully sent to API!");
                setMessageType("success");
            }
        } catch (apiError) {
            console.error("API Post Error:", apiError);
            const status = apiError?.status || apiError?.response?.status;
            const backendMsg = apiError?.data?.Message || apiError?.response?.data?.Message;
            let finalMsg = "";
            if (status) finalMsg += `HTTP Error! Status: ${status}. `;
            if (backendMsg) finalMsg += backendMsg;
            if (!finalMsg) finalMsg = apiError?.message || "Failed to send data to API.";
            setMessage(finalMsg);
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };

const parsePDF = async (arrayBuffer) => {
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let allLines = [];

    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        
        const linesOnPage = content.items.reduce((acc, item) => {
            const y = Math.round(item.transform[5]);
            if (!acc[y]) acc[y] = [];
            acc[y].push({ text: item.str.trim(), x: item.transform[4] });
            return acc;
        }, {});

        const sortedLines = Object.keys(linesOnPage)
            .sort((a, b) => b - a)
            .map(y =>
                linesOnPage[y]
                    .sort((a, b) => a.x - b.x)
                    .map(item => item.text)
                    .join(" ")
            )
            .filter(line => line.trim() !== "");

        allLines.push(...sortedLines);
    }
    
    const fullText = allLines.join("\n");
    const safeMatch = (regex, text, group = 1) => {
        const m = text.match(regex);
        return m ? (m[group] || "").trim() : "";
    };

    const commonDetails = {
        "Customer Name": safeMatch(/^([^\n]*Ashok\s+Leyland\s+Ltd[^\n]*)/im, fullText) || "Ashok Leyland Ltd",
        "Customer Address": safeMatch(/Plot\s*No\.?77\s*Electronic\s*complex\s*Hosur\s*PIN:?635109/i, fullText) || "Alwar, Matsya Industrial Area, Alwar PIN:301030",
        "Voucher No": safeMatch(/Voucher\s*\.?\s*No\s*:\s*(\d+)/i, fullText),
        "Voucher Date": safeMatch(/Date\s*:\s*(\d{2}\.\d{2}\.\d{4})/i, fullText),
        "Cheque No": safeMatch(/Cheque\s*\.?\s*No\s*:\s*(\d+)/i, fullText),
        "Supplier Details": safeMatch(/SP\s*PIPE\s*PRIVATE\s*LIMITED/i, fullText),
    };

    const sumTotalMatch = fullText.match(/Sum\s*Total\s*:\s*([\d,]+\.\d{2})/i);
    const sumTotal = sumTotalMatch ? parseFloat(sumTotalMatch[1].replace(/,/g, "")) : 0.0;
    
    const docTypeRegex = /^(KZ|RE|KG)\s/;
    const dataStartIndex = allLines.findIndex(line => docTypeRegex.test(line.trim()));
    const sumTotalIndex = allLines.findIndex(line => line.match(/Sum\s*Total:/i));

    if (dataStartIndex === -1) {
        throw new Error("Could not find the start of table data in the PDF.");
    }

    const tableLines = allLines.slice(dataStartIndex, sumTotalIndex > -1 ? sumTotalIndex : undefined);
    
    let rowBlocks = [];
    let currentBlock = "";
    
    for (const line of tableLines) {
        if (docTypeRegex.test(line.trim())) {
            if (currentBlock) rowBlocks.push(currentBlock.trim());
            currentBlock = line + " ";
        } else {
            currentBlock += line + " ";
        }
    }
    if (currentBlock) rowBlocks.push(currentBlock.trim());

    const allRows = [];
    
    for (let i = 0; i < rowBlocks.length; i++) {
        const block = rowBlocks[i];
        const docType = safeMatch(/^(KZ|RE|KG)/, block, 0) || "";
        let parsedRow = {};
        
        if (docType === "KZ") {
    const docNo = safeMatch(/KZ\s*(\d+)/, block);
    const postingDate = safeMatch(/\d+\s*(\d{2}\.\d{2}\.\d{4})/, block);
    
    // Correctly match the CMI Code (PREPAYMENT) and Doc.Text (the numeric value)
    const cmiCode = safeMatch(/(PREPAYMENT)/i, block);
    const docText = safeMatch(/(\d{1,3}(?:,\d{3})*\.\d{2,3})/, block);
    
    // The Bill.No is the same as the PCDV number, and the Bill.Date follows it.
    const billNo = safeMatch(/(PCDV\d{6})/i, block);
    const billDate = safeMatch(/(?<=PCDV\d{6}\s)(\d{2}\.\d{2}\.\d{4})/, block);

    // Extract gross, tds, and net amounts
    const grossMatch = block.match(/(\d[\d,]*\.\d{2})/g);
    const gross = grossMatch ? parseFloat(grossMatch[0].replace(/,/g, "")) : 0;
    const netAmt = grossMatch ? parseFloat(grossMatch[1].replace(/,/g, "")) : 0;
    const tds = gross - netAmt;

    parsedRow = {
        "Doc. Type": docType,
        "Document No": docNo,
        "Posting Date": postingDate && postingDate !== "-" ? postingDate : getCurrentDate(),
        "CMI Code": cmiCode || "N/A",
        "Doc.Text": "-",
        "Bill.No": billNo || "N/A",
        "Bill.Date": billDate && billDate !== "-" ? billDate : getCurrentDate(),
        "Gross": gross.toFixed(2),
        "TDS": tds.toFixed(2),
        "Net.Amt": netAmt.toFixed(2),
        "D/C": "D",
    };
}else if (docType === "KG") {
            const docNo = safeMatch(/KG\s*(\d{8,12})/, block);
            const postingDate = safeMatch(/\d{8,12}\s*(\d{2}\.\d{2}\.\d{4})/, block);
            
            const billNoMatch = block.match(/(DN\/SP\/\d{2}-\d{2}\/\d+)|(CN\/\d{2}-\d{2}\/\d+)|(SP\/\d{2}-\d{2}\/\d+)/i);
            const billNo = billNoMatch ? billNoMatch[0].trim() : "N/A";
            
            let billDate = "-";
            if (billNoMatch) {
                const billNoIndex = block.indexOf(billNo);
                const afterBillNo = block.substring(billNoIndex + billNo.length);
                const dateMatch = afterBillNo.match(/\d{2}\.\d{2}\.\d{4}/);
                if (dateMatch) billDate = dateMatch[0];
            }
            
            const numericValues = (block.match(/(\d[\d,]*\.\d{2})/g) || []).map(n =>
                parseFloat(n.replace(/,/g, ""))
            ).sort((a, b) => b - a);
            
            let gross = numericValues[0] || 0.0;
            let netAmt = numericValues.length > 1 ? numericValues[1] : gross;
            if (netAmt > gross) [gross, netAmt] = [netAmt, gross];

            let tds = (gross - netAmt);
            const dc = tds < 0.01 ? "D" : "C";

            parsedRow = {
                "Doc. Type": docType,
                "Document No": docNo,
                "Posting Date": postingDate && postingDate !== "-" ? postingDate : getCurrentDate(),
                "CMI Code": "-",
                "Doc.Text": "-",
                "Bill.No": billNo || "N/A",
                "Bill.Date": billDate && billDate !== "-" ? billDate : getCurrentDate(),
                "Gross": gross.toFixed(2),
                "TDS": tds.toFixed(2),
                "Net.Amt": netAmt.toFixed(2),
                "D/C": dc,
            };
        } else if (docType === "RE") {
            const docNo = safeMatch(/RE\s*(5\d{8,9})/, block);
            const postingDate = safeMatch(/5\d{8,9}\s*(\d{2}\.\d{2}\.\d{4})/, block);

            const billNoMatch = block.match(/SP\/\d{2}-\d{2}\/\d{4,}/);
            const billNo = billNoMatch ? billNoMatch[0].trim() : "-";

            let billDate = "-";
            if (billNoMatch) {
                const billNoIndex = block.indexOf(billNo);
                const afterBillNo = block.substring(billNoIndex + billNo.length);
                const dateMatch = afterBillNo.match(/\d{2}\.\d{2}\.\d{4}/);
                if (dateMatch) billDate = dateMatch[0];
            }
            
            // CUSTOM FIX FOR THE FINAL ROW
            if (i === rowBlocks.length - 1) {
                const parts = block.split(/\s+/).filter(part => part.trim() !== "");
                let gross = 0, netAmt = 0, tds = 0;
                
                // Find and assign values from the last RE row
                const numericParts = parts.filter(p => p.match(/^[\d,]+\.\d{2}$/));
                if (numericParts.length >= 3) {
                    gross = parseFloat(numericParts[0].replace(/,/g, ""));
                    netAmt = parseFloat(numericParts[1].replace(/,/g, ""));
                    tds = parseFloat(numericParts[2].replace(/,/g, ""));
                } else if (numericParts.length >= 2) {
                    gross = parseFloat(numericParts[0].replace(/,/g, ""));
                    netAmt = parseFloat(numericParts[1].replace(/,/g, ""));
                    tds = parseFloat(gross - netAmt);
                }

                parsedRow = {
                    "Doc. Type": docType,
                    "Document No": docNo,
                    "Posting Date":postingDate && postingDate !== "-" ? postingDate : getCurrentDate(),
                    "CMI Code": "-",
                    "Doc.Text": "-",
                    "Bill.No": billNo || "N/A",
                    "Bill.Date": billDate && billDate !== "-" ? billDate : getCurrentDate(),
                    "Gross": gross.toFixed(2),
                    "TDS": tds.toFixed(2),
                    "Net.Amt": netAmt.toFixed(2),
                    "D/C": "C", // The last row seems to always be 'C' for this document
                };
            } else {
                // ORIGINAL LOGIC FOR ALL OTHER RE ROWS
                const numericValues = (block.match(/(\d[\d,]*\.\d{2})/g) || []).map(n =>
                    parseFloat(n.replace(/,/g, ""))
                ).sort((a, b) => b - a);
                
                let gross = numericValues[0] || 0.0;
                let netAmt = numericValues.length > 1 ? numericValues[1] : gross;
                if (netAmt > gross) [gross, netAmt] = [netAmt, gross];

                let tds = (gross - netAmt);
                const dc = tds < 0.01 ? "D" : "C";

                parsedRow = {
                    "Doc. Type": docType,
                    "Document No": docNo,
                    "Posting Date":postingDate && postingDate !== "-" ? postingDate : getCurrentDate(),
                    "CMI Code": "-",
                    "Doc.Text": "-",
                    "Bill.No": billNo || "N/A",
                    "Bill.Date": billDate && billDate !== "-" ? billDate : getCurrentDate(),
                    "Gross": gross.toFixed(2),
                    "TDS": tds.toFixed(2),
                    "Net.Amt": netAmt.toFixed(2),
                    "D/C": dc,
                };
            }
        }

        allRows.push({ ...commonDetails, ...parsedRow });
    }
    
    const filteredRows = allRows.filter(row => !(parseFloat(row["Gross"]) === 0 && parseFloat(row["Net.Amt"]) === 0));
    const finalRows = filteredRows.map((row, index) => ({ ...row, "S.No": index + 1 }));

    return { extractedRows: finalRows, total: sumTotal.toFixed(2), commonDetails };
};

const getCurrentDate = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
};

    const getMessageClasses = () => {
        let base = "fixed top-4 right-4 p-3 rounded-lg shadow-lg text-xs font-medium flex items-center transition-all duration-300 transform z-50 ";
        if (messageType === "success") base += "bg-emerald-600 text-white translate-x-0 opacity-100";
        else if (messageType === "error") base += "bg-red-600 text-white translate-x-0 opacity-100";
        else base += "bg-gray-700 text-gray-300 translate-x-0 opacity-100";
        if (!message) base += " translate-x-full opacity-0";
        return base;
    };

    return (
        <div className="bg-gray-100 text-gray-900 min-h-screen w-full flex flex-col items-center font-sans relative">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-5xl flex flex-col items-center mt-20">
                <div className="flex justify-between items-center w-full mb-6">
                    <h1 className="text-2xl font-extrabold text-gray-800 flex items-center">
                        <button
                            onClick={() => navigate("/Menu")}
                            className="p-2 rounded-full text-gray-700 hover:bg-gray-200 transition-colors duration-300 mr-2"
                            aria-label="Back to Menu"
                        >
                            <FaArrowLeft className="text-xl" />
                        </button>
                        <FaFileInvoiceDollar className="mr-2 text-cyan-600" /> PDF Extractor
                    </h1>
                    <div className="flex items-center space-x-3">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                        <button
                            onClick={() => fileInputRef.current.click()}
                            disabled={!isPdfJsLoaded || loading}
                            className="flex items-center justify-center h-10 px-4 rounded-full bg-cyan-600 text-white text-sm font-semibold hover:bg-cyan-700 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md transform hover:scale-105"
                        >
                            <FaCloudUploadAlt className="mr-2 text-sm" />
                            {file ? `Change File: ${file.name}` : "Browse PDF"}
                        </button>
                        {tableData.length > 0 && (
                            <button
                                onClick={handlePostData}
                                disabled={loading}
                                className="flex items-center justify-center h-10 px-4 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md transform hover:scale-105"
                            >
                                <FaCloudDownloadAlt className="mr-2 text-sm" /> Push to DB
                            </button>
                        )}
                    </div>
                </div>

                {message && (
                    <div className={getMessageClasses()}>
                        <span className="flex items-center">
                            {messageType === "success" && <FaCheckCircle className="mr-2" />}
                            {messageType === "error" && <FaTimesCircle className="mr-2" />}
                            {messageType === "info" && <BiFile className="mr-2" />}
                            {message}
                        </span>
                    </div>
                )}
                {receiptData && (
                    <div className="w-full max-w-5xl bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 shadow-md">
                        <h2 className="text-xl font-bold text-gray-800 flex items-center border-b pb-2 border-gray-200 mb-2">
                            <FaReceipt className="mr-2 text-pink-500" /> Receipt Details
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-600 mb-4">
                            <p><span className="font-semibold text-gray-700">Customer Name:</span> {receiptData["Customer Name"]}</p>
                            <p><span className="font-semibold text-gray-700">Customer Address:</span> {receiptData["Customer Address"]}</p>
                            <p><span className="font-semibold text-gray-700">Voucher No:</span> {receiptData["Voucher No"]}</p>
                            <p><span className="font-semibold text-gray-700">Voucher Date:</span> {receiptData["Voucher Date"]}</p>
                            <p><span className="font-semibold text-gray-700">Cheque No:</span> {receiptData["Cheque No"]}</p>
                            <p><span className="font-semibold text-gray-700">Supplier Details:</span> {receiptData["Supplier Details"]}</p>
                        </div>
                        <div className="flex justify-end items-center pt-2 border-t border-gray-200 mt-2">
                            <span className="font-bold text-lg text-gray-800 mr-2 flex items-center">
                                <FaTags className="mr-1 text-yellow-500" /> Sum Total:
                            </span>
                            <span className="text-xl text-cyan-600 font-extrabold tracking-wide">
                                ₹ {sumTotal}
                            </span>
                        </div>
                    </div>
                )}
                {tableData.length > 0 && (
                    <div className="overflow-x-auto w-full mt-2 border border-gray-200 rounded-lg shadow-inner">
                        <table className="w-full table-auto text-xs">
                            <thead className="bg-gray-200 text-gray-800 border-b border-gray-300">
                                <tr className="h-8">
                                    {tableHeaders.map(h => (
                                        <th key={h} className="px-2 py-1 text-left font-semibold whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.map((row, i) => (
                                    <tr key={i} className={`h-8 border-b border-gray-200 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                        {tableHeaders.map(h => (
                                            <td key={h} className="px-2 py-1 text-gray-600">
                                                {row[h]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadPage;